import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Check, ChevronDown, Send, Trash2, X } from 'lucide-react'
import { Modal } from './ui/Modal'
import { Button } from './ui/Button'
import { ConfirmDialog } from './ui/ConfirmDialog'
import { api, extractApiError } from '../lib/api'
import { useAuthStore } from '../stores/authStore'
import {
  space as spaceSchema,
  spaceMembersResponse,
  teammatesResponse,
  workspaceMembersResponse,
} from '../lib/types'
import { ACCESS_LEVEL_OPTIONS as ACCESS_OPTIONS } from '../lib/access-levels'

const AVATAR_PALETTE = [
  '#02367B',
  '#34D399',
  '#F87171',
  '#FBBF24',
  '#A78BFA',
  '#F472B6',
  '#0a2349',
  '#FB923C',
]
function avatarColor(name) {
  let hash = 0
  for (let i = 0; i < name.length; i++)
    hash = (hash * 31 + name.charCodeAt(i)) | 0
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length]
}
function initials(name) {
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

const WORK_ROLE_SLUG_LABEL = {
  manager: 'Manager',
  team_leader: 'Team Leader',
  employee: 'Employee',
}
/** Resolved Work Role label — the catalogue name, else the tier slug's
 *  label. */
function roleLabelOf(u) {
  return u.workRoleName ?? WORK_ROLE_SLUG_LABEL[u.workRole] ?? null
}
/** Small Work-role + Department tag pills under a candidate's email so a
 *  picker listing several same-named teammates can be told apart. The Work
 *  Role pill is accent-tinted; the Department pill is neutral. Renders
 *  nothing when neither is known. */
function MemberTags({ u }) {
  const role = roleLabelOf(u)
  const dept = u.departmentName ?? null
  if (!role && !dept) return null
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1.5">
      {role ? (
        <span className="text-xs text-[color:var(--color-ink-muted)]">
          {role}
        </span>
      ) : null}
      {dept ? (
        <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium bg-[color:var(--color-card-hover)] text-[color:var(--color-ink)] border border-[color:var(--color-rule)]">
          {dept}
        </span>
      ) : null}
    </div>
  )
}

/** Sharing & Permissions for a single Space.
 *
 *  Visual twin of `WorkspacePeopleDialog`: same modal sizing, chip-style
 *  input area, "People with access" section, and per-row remove. The
 *  semantic difference is that Space membership is binary and only
 *  workspace members are eligible — there's no access-level dropdown
 *  and no email invite (workspace invite handles that one layer up). */
export function SpaceShareDialog({
  open,
  spaceId,
  workspaceId,
  spaceName,
  onClose,
  /** Caller's hint that this is a Personal Space (owner-only, unshareable).
   *  Optional: the dialog also derives it from the fetched Space, so the guard
   *  holds even when the caller doesn't pass it. */
  isPersonal,
}) {
  const qc = useQueryClient()
  // [External Collaboration] A planning-handler external (Manager / Team Leader)
  // MANAGES access within their grant exactly like an internal one — the backend
  // `assertSpaceManager` admits them — so the per-row access pickers + remove are
  // enabled (see `canManageAccess`). The one thing they can't do is pull in
  // brand-new people: the "add from your workspace" roster (`/workspaces/:id/members`)
  // + teammates fetch 403 for a member with no workspace membership, so those two
  // queries stay disabled and the add field is hidden for externals. The "People
  // with access" list + the Owner row are sourced from the space payload, which
  // returns 200 for a reachable external.
  const isExternalMember = useAuthStore((s) => s.user?.membershipType === 'external')

  // Picker state — selected workspace members become chips; the free
  // text below filters the remaining candidate list.
  const [selected, setSelected] = useState([])
  const [query, setQuery] = useState('')
  const [accessLevel, setAccessLevel] = useState('edit')
  const [accessMenuOpen, setAccessMenuOpen] = useState(false)
  // Fixed-position coords for the portaled access-level menu. Computed
  // off the trigger's bounding rect so the menu sits flush with the
  // trigger button regardless of where the modal is on screen.
  const [accessMenuCoords, setAccessMenuCoords] = useState(null)
  const inputRef = useRef(null)
  const accessTriggerRef = useRef(null)
  const accessMenuRef = useRef(null)
  const [removeTarget, setRemoveTarget] = useState(null)
  // Tracks whether the user changed any member's access level this session.
  // Access edits auto-save instantly (updateAccessMutation), but per the QA
  // ticket the success toast should fire only when the user explicitly clicks
  // "Done" — not on every dropdown pick. `handleDone` reads this flag to
  // decide whether to show that single confirmation.
  const [accessChanged, setAccessChanged] = useState(false)

  // Reset transient state every time the dialog reopens.
  useEffect(() => {
    if (open) {
      setSelected([])
      setQuery('')
      setAccessLevel('edit')
      setAccessMenuOpen(false)
      setAccessChanged(false)
    }
  }, [open])

  // Close the access dropdown on outside click / Escape.
  useEffect(() => {
    if (!accessMenuOpen) return
    function onClick(e) {
      const t = e.target
      if (
        !accessMenuRef.current?.contains(t) &&
        !accessTriggerRef.current?.contains(t)
      ) {
        setAccessMenuOpen(false)
      }
    }
    function onKey(e) {
      if (e.key === 'Escape') setAccessMenuOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [accessMenuOpen])

  // Compute the portaled menu's coordinates each time it opens. The
  // panel is right-anchored under the trigger (its right edge aligns
  // with the trigger's right edge), with a small vertical gap. We
  // also clamp to the viewport so the menu never spills below the
  // bottom edge — when there isn't enough room down we flip above the
  // trigger. The previous build used `position: absolute` inside the
  // dialog's scroll container, which clipped the menu to the modal
  // body and produced the "dropdown half-hidden" visual the user
  // reported.
  useLayoutEffect(() => {
    if (!accessMenuOpen) {
      setAccessMenuCoords(null)
      return
    }
    const r = accessTriggerRef.current?.getBoundingClientRect()
    if (!r) return
    const PANEL_W = 288 // matches `w-72` below (18rem × 16px = 288px)
    const GAP = 6
    const margin = 8
    const viewportW =
      document.documentElement?.clientWidth ?? window.innerWidth
    const viewportH = window.innerHeight
    // Right-anchored: align panel.right with trigger.right.
    let left = r.right - PANEL_W
    // Clamp to viewport so the menu can't fall off either edge.
    if (left + PANEL_W > viewportW - margin) {
      left = viewportW - margin - PANEL_W
    }
    if (left < margin) left = margin
    // Estimate menu height — three rows × ~64px each + chrome. We
    // refine after first paint when we have the real height, but a
    // sensible default avoids a flicker.
    const estimatedH = 220
    let top = r.bottom + GAP
    if (top + estimatedH > viewportH - margin) {
      // Not enough room down — flip above the trigger.
      const above = r.top - GAP - estimatedH
      if (above >= margin) top = above
      else top = Math.max(margin, viewportH - margin - estimatedH)
    }
    setAccessMenuCoords({ top, left })
  }, [accessMenuOpen])

  // ---- Queries ----------------------------------------------------------

  const membersQuery = useQuery({
    queryKey: ['space-members', spaceId],
    queryFn: async () => {
      const res = await api.get(`/spaces/${spaceId}/members`)
      return spaceMembersResponse.parse(res.data)
    },
    enabled: open && Boolean(spaceId),
  })

  const wsMembersQuery = useQuery({
    queryKey: ['workspace-members', workspaceId],
    queryFn: async () => {
      const res = await api.get(`/workspaces/${workspaceId}/members`)
      return workspaceMembersResponse.parse(res.data)
    },
    enabled: open && Boolean(workspaceId) && !isExternalMember,
  })

  // Fetch the Space itself so we know who created it. The owner row at
  // the top of "People with access" is rendered against this. Declared
  // BEFORE invitableExternalsQuery, which reads `spaceQuery.data?.isPrivate`
  // in its `enabled`: a forward reference to this `const` throws a
  // temporal-dead-zone ReferenceError that crashes the whole dialog for
  // internal members (externals short-circuit on `!isExternalMember` first).
  const spaceQuery = useQuery({
    queryKey: ['space', spaceId],
    queryFn: async () => {
      const res = await api.get(`/spaces/${spaceId}`)
      return spaceSchema.parse(res.data)
    },
    enabled: open && Boolean(spaceId),
  })

  // [External Collaboration] External collaborators who can still be invited
  // HERE. They hold no WorkspaceMember row, so the workspace roster above can
  // never list them — which is why they were missing from this picker.
  //
  // PRIVATE Spaces only, matching the server: a department grant already
  // reaches every PUBLIC Space in that department, so an external who belongs
  // there can open it already and the endpoint returns nothing. For a private
  // Space the department grant deliberately does not reach, and the direct
  // Space grant this add writes IS the invitation.
  const invitableExternalsQuery = useQuery({
    queryKey: ['invitable-externals', spaceId],
    queryFn: async () => {
      const res = await api.get(`/spaces/${spaceId}/invitable-externals`)
      // Same row shape as the workspace roster (the server builds it that
      // way), so the same schema parses it — including `external`, which the
      // badge below reads.
      return workspaceMembersResponse.parse(res.data)
    },
    enabled:
      open &&
      Boolean(spaceId) &&
      !isExternalMember &&
      spaceQuery.data?.isPrivate === true,
  })

  // Pull the actor's own department off `/users/teammates` (the response
  // surfaces it at the top level). Department membership is per-workspace
  // now, so the call is scoped to this Space's workspace. We only fetch
  // when the dialog is open — no hit on the API otherwise.
  const teammatesQuery = useQuery({
    queryKey: ['teammates', workspaceId],
    queryFn: async () => {
      const res = await api.get('/users/teammates', {
        params: { workspaceId },
      })
      return teammatesResponse.parse(res.data)
    },
    enabled: open && Boolean(workspaceId) && !isExternalMember,
  })

  // Workspace-scope member viewers + Company / Platform Admins bypass
  // the dept filter so they can invite cross-department. Gated on the
  // `members.view_workspace` operation rather than tier — keeps the
  // dialog in lock-step with the Members directory backend check
  // when a tenant rewires what their Manager role can see.
  const actor = useAuthStore((s) => s.user)
  const hasWorkspaceMembersOp = useAuthStore((s) =>
    s.hasOperation('members.view_workspace'),
  )
  // Input to "can this person CHANGE access?" — a platform-admin grant
  // (a Sub Admin granted workspaces.manage). Combined with the Company-Admin
  // role and Space ownership in `canManageAccess` below. Mirrors the backend
  // `isPlatformAdmin` / `assertSpaceManager` gate.
  const hasWorkspacesManageGrant = useAuthStore((s) =>
    s.hasSubAdminOperation('workspaces.manage'),
  )
  const actorBypassesDeptFilter = useMemo(() => {
    if (!actor) return false
    if (actor.role === 'company_admin' || actor.role === 'super_admin') return true
    return hasWorkspaceMembersOp
  }, [actor, hasWorkspaceMembersOp])
  const actorDepartmentId = teammatesQuery.data?.departmentId ?? null

  // Per-Space access level — sent to the backend as `accessLevel` on
  // the add-members POST. The level lands on `SpaceMember.accessLevel`
  // (per-Space override) so it works regardless of the actor's
  // workspace-level admin status. Available to anyone allowed to
  // share the Space, no admin gate required.

  const owner = useMemo(() => {
    const creatorId = spaceQuery.data?.createdById ?? null
    if (!creatorId) return null
    return (
      wsMembersQuery.data?.data?.find((m) => m.userId === creatorId) ??
      // Fallback: the members list now carries every member of a public
      // Space (with tags), so the owner row still renders even when the
      // viewer's own roster doesn't include them.
      membersQuery.data?.data?.find((m) => m.userId === creatorId) ??
      // Final fallback: the single-space payload resolves the creator's name
      // across ALL tenant users, so the Owner row still renders when the
      // viewer can't fetch a roster that carries them — notably an external
      // collaborator, whose `/workspaces/:id/members` call 403s. The avatar
      // falls back to the initial monogram.
      (spaceQuery.data?.createdByName
        ? {
            userId: creatorId,
            fullName: spaceQuery.data.createdByName,
            emailId: spaceQuery.data.createdByEmail ?? null,
            avatarUrl: spaceQuery.data.createdByAvatarUrl ?? null,
            workRoleName: spaceQuery.data.createdByWorkRoleName ?? null,
            departmentName: spaceQuery.data.createdByDepartmentName ?? null,
          }
        : null)
    )
  }, [spaceQuery.data, wsMembersQuery.data, membersQuery.data])

  // Who may CHANGE access / membership (add, remove, set access level):
  // ONLY the Space owner or a platform admin (Company Admin / Sub Admin with
  // workspaces.manage). Everyone else who can open the dialog — including
  // department-wide Managers / Team Leaders and plain editors — sees the list
  // READ-ONLY. Mirrors the backend `assertSpaceManager` gate.
  const canManageAccess = useMemo(() => {
    if (!actor) return false
    // Server-resolved flag is authoritative — it also grants a cross-department
    // member department-scoped management (invite their own team). Fall back to
    // the owner/admin heuristic only if the payload predates the field.
    const flag = spaceQuery.data?.myCanManageMembers
    if (typeof flag === 'boolean') return flag
    if (spaceQuery.data?.createdById === actor.id) return true
    return actor.role === 'company_admin' || hasWorkspacesManageGrant
  }, [actor, spaceQuery.data, hasWorkspacesManageGrant])

  // Department-scoped manager (a cross-department member): the picker + the
  // backend restrict who they can add to their OWN department. Null for a full
  // manager (owner / admin), who may add anyone.
  const manageDepartmentId = spaceQuery.data?.myManageDepartmentId ?? null

  // A Personal Space is PRIVATE TO ITS OWNER and can never be shared — the
  // backend rejects `POST /spaces/:id/members` outright. The entry point is
  // already hidden (SpaceRowActions), so this is the defensive second line:
  // it keeps the dialog shut if it is ever reached through stale state, a
  // cached payload, direct component use or deep navigation. Derived from the
  // freshly-fetched Space, with the caller-supplied hint as a fallback for the
  // window before that query resolves.
  const isPersonalSpace =
    spaceQuery.data?.isPersonal === true || isPersonal === true

  // Self-close rather than sitting open-but-blank if it was opened in error.
  useEffect(() => {
    if (open && isPersonalSpace) onClose?.()
  }, [open, isPersonalSpace, onClose])

  // Adding BRAND-NEW people needs the workspace roster (`/workspaces/:id/members`),
  // which 403s for an external (no workspace membership). So externals manage the
  // people already on the Space (change access / remove) but don't get the add
  // field — there's no candidate roster we can safely fetch for them.
  const canAddMembers = canManageAccess && !isExternalMember

  // Workspace members not yet on the Space, with the in-progress chip
  // selections excluded too, filtered to the actor's department for
  // employees / team leaders, and matched against the search query.
  const candidates = useMemo(() => {
    const onSpace = new Set(
      (membersQuery.data?.data ?? []).map((m) => m.userId),
    )
    const all = [
      ...(wsMembersQuery.data?.data ?? []),
      ...(invitableExternalsQuery.data?.data ?? []),
    ]
    const trimmed = query.trim().toLowerCase()
    return all
      .filter(
        (u) =>
          // Exclude self — you can't share a Space with yourself; you
          // already have access (and are typically the owner/sharer). Selected
          // people are NOT excluded here — they stay in the list with a ticked
          // checkbox so the selection can be toggled off again.
          u.userId !== actor?.id &&
          !onSpace.has(u.userId),
      )
      .filter((u) => {
        // A DEPARTMENT-SCOPED manager (a cross-department member) may only add
        // their OWN department's members — the backend enforces this too, so
        // the picker mirrors it rather than offering people the add would 403.
        if (manageDepartmentId) {
          return String(u.departmentId) === String(manageDepartmentId)
        }
        // FULL managers can add anyone in the workspace. The dept filter below
        // only narrows the list for read-only viewers who happen to browse it.
        // Members whose own departmentId is null (company-wide) always show.
        if (canAddMembers) return true
        if (actorBypassesDeptFilter) return true
        if (!actorDepartmentId) return true
        if (u.departmentId == null) return true
        return u.departmentId === actorDepartmentId
      })
      .filter((u) => {
        if (!trimmed) return true
        return (
          u.fullName.toLowerCase().includes(trimmed) ||
          u.emailId.toLowerCase().includes(trimmed)
        )
      })
  }, [
    membersQuery.data,
    wsMembersQuery.data,
    invitableExternalsQuery.data,
    query,
    actor?.id,
    canAddMembers,
    manageDepartmentId,
    actorBypassesDeptFilter,
    actorDepartmentId,
  ])

  // Multi-select helpers for the candidate checkboxes + "Select all".
  const selectedIds = useMemo(
    () => new Set(selected.map((m) => m.userId)),
    [selected],
  )
  const allCandidatesSelected =
    candidates.length > 0 && candidates.every((u) => selectedIds.has(u.userId))
  const someCandidatesSelected = candidates.some((u) =>
    selectedIds.has(u.userId),
  )
  function toggleCandidate(m) {
    setSelected((prev) =>
      prev.some((p) => p.userId === m.userId)
        ? prev.filter((p) => p.userId !== m.userId)
        : [...prev, m],
    )
  }
  function toggleSelectAllCandidates() {
    if (allCandidatesSelected) {
      // Untick only the currently-visible candidates (respects the search
      // filter) — any selection outside the filtered view is left intact.
      const visible = new Set(candidates.map((u) => u.userId))
      setSelected((prev) => prev.filter((m) => !visible.has(m.userId)))
    } else {
      setSelected((prev) => {
        const have = new Set(prev.map((m) => m.userId))
        return [...prev, ...candidates.filter((u) => !have.has(u.userId))]
      })
    }
  }

  // ---- Mutations --------------------------------------------------------

  const addMutation = useMutation({
    mutationFn: async (payload) => {
      // Single call — the backend stores `accessLevel` on the new
      // `SpaceMember` row as a per-Space override. No need for a
      // follow-up PATCH on workspace_members; the per-Space level
      // wins inside this Space regardless of the actor's
      // workspace-admin status.
      const res = await api.post(`/spaces/${spaceId}/members`, {
        userIds: payload.userIds,
        accessLevel: payload.accessLevel,
      })
      return res.data
    },
    onSuccess: (result) => {
      if (result.added === 0) {
        toast.info('Selected people are already on this Space or have a pending invitation.')
      } else {
        const levelLabel =
          ACCESS_OPTIONS.find((o) => o.value === accessLevel)?.label ?? 'Edit'
        // Adding someone sends a Space INVITATION (the SpaceMember row is
        // created `pending` + an in-app/email invite) — they only join once
        // they accept. So the message must say "invited", not "added". Wording
        // mirrors the invite notification ("invited you to join").
        toast.success(
          `${result.added} teammate${result.added === 1 ? '' : 's'} invited to join ${spaceName} (${levelLabel}).`,
        )
      }
      void qc.invalidateQueries({ queryKey: ['space-members', spaceId] })
      void qc.invalidateQueries({ queryKey: ['workspace-members', workspaceId] })
      setSelected([])
      setQuery('')
      onClose()
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const removeMutation = useMutation({
    mutationFn: async (userId) => {
      await api.delete(`/spaces/${spaceId}/members/${userId}`)
    },
    onSuccess: () => {
      toast.success('Member removed from Space.')
      void qc.invalidateQueries({ queryKey: ['space-members', spaceId] })
      // Removal cascade-unassigns the member from this Space's tasks, so refresh
      // the task list / board to drop their assignee avatar without a reload.
      void qc.invalidateQueries({ queryKey: ['tasks'] })
      setRemoveTarget(null)
    },
    onError: (err) => {
      toast.error(extractApiError(err).message)
      setRemoveTarget(null)
    },
  })

  /** Update an existing member's per-Space access level. Targets
   *  `SpaceMember.accessLevel` only — the user's workspace-level access
   *  stays untouched, so they keep whatever access they have in other
   *  Spaces. */
  const updateAccessMutation = useMutation({
    mutationFn: async (payload) => {
      await api.patch(
        `/spaces/${spaceId}/members/${payload.userId}`,
        { accessLevel: payload.accessLevel },
      )
      return payload
    },
    onSuccess: () => {
      // Auto-save stays instant, but the success toast is deferred to the
      // explicit "Done" click (see handleDone) so it doesn't fire on every
      // dropdown pick. We still refresh the roster immediately — that's the
      // user's live feedback that the pick landed. Errors DO still toast right
      // away (onError) — a failed save must be surfaced.
      //
      // NB: do NOT toast here. A `toast.success('Access level updated
      // successfully.')` call used to sit on this line, which meant BOTH the
      // per-pick toast and the deferred "Done" toast fired (QA) — the deferred
      // path was added but the eager one never removed.
      setAccessChanged(true)
      void qc.invalidateQueries({ queryKey: ['space-members', spaceId] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  // ---- Helpers ----------------------------------------------------------

  function removeChip(userId) {
    setSelected((prev) => prev.filter((m) => m.userId !== userId))
  }
  function send() {
    if (selected.length === 0) {
      inputRef.current?.focus()
      return
    }
    addMutation.mutate({
      userIds: selected.map((m) => m.userId),
      accessLevel,
    })
  }
  // "Done" just closes the dialog (access edits already auto-saved), but it's
  // also the intentional confirmation point: if the user changed any access
  // level this session, surface ONE success toast here instead of on each
  // pick. Closing via ✕ / backdrop / Esc routes through `onClose` directly and
  // stays silent, matching "the message shows only after the user clicks Done".
  function handleDone() {
    if (accessChanged) toast.success('Access updated.')
    onClose()
  }

  // ---- Render -----------------------------------------------------------

  const members = membersQuery.data?.data ?? []
  // Don't double-render the owner — they get their own row at the top.
  const sortedMembers = useMemo(
    () =>
      [...members]
        .filter((m) => !owner || m.userId !== owner.userId)
        .sort((a, b) => a.fullName.localeCompare(b.fullName)),
    [members, owner],
  )
  // Whether the trailing "remove" column is needed. Managers can now remove
  // ANYONE — removing an inherited member of a public Space records an
  // exclusion server-side — so the column shows whenever the viewer can
  // manage and there's at least one member. Read-only viewers get no column.
  const anyRemovable = useMemo(
    () => canManageAccess && sortedMembers.length > 0,
    [canManageAccess, sortedMembers],
  )
  /** Lookup each Space member's effective access level for the per-row
   *  dropdown. Prefers the per-Space override on `SpaceMember.accessLevel`;
   *  falls back to the workspace-level access when the override is null
   *  so the dropdown reflects what the user can actually do inside this
   *  Space. Final fallback is 'edit' until queries resolve. */
  const accessByUserId = useMemo(() => {
    const wsMap = new Map()
    for (const w of wsMembersQuery.data?.data ?? []) {
      wsMap.set(w.userId, w.accessLevel ?? 'edit')
    }
    const map = new Map()
    for (const m of membersQuery.data?.data ?? []) {
      map.set(m.userId, m.accessLevel ?? wsMap.get(m.userId) ?? 'edit')
    }
    return map
  }, [membersQuery.data, wsMembersQuery.data])

  // Work-role + department metadata keyed by userId, sourced from the
  // workspace-members query (which carries workRoleName / departmentName).
  // The space-members payload behind "People with access" doesn't include
  // those fields, so we look them up here to show the same tag pills.
  const wsMetaByUserId = useMemo(() => {
    const map = new Map()
    for (const w of wsMembersQuery.data?.data ?? []) map.set(w.userId, w)
    return map
  }, [wsMembersQuery.data])

  // Two modes — same dual-mode pattern as WorkspacePeopleDialog:
  //   idle    → show "People with access" + Done
  //   inviting → show candidate dropdown + Cancel / Add
  const inviting = selected.length > 0 || query.trim().length > 0

  // Personal Space: never render the sharing UI (see `isPersonalSpace`). Placed
  // AFTER every hook so the hook order stays stable on the render where the
  // Space payload resolves and this flips.
  if (isPersonalSpace) return null

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={`Share "${spaceName}"`}
        widthClass="max-w-[500px]"
        footer={
          inviting ? (
            <>
              <Button
                variant="secondary"
                onClick={onClose}
                disabled={addMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="button"
                loading={addMutation.isPending}
                disabled={selected.length === 0}
                onClick={send}
              >
                <Send size={14} />
                Send
              </Button>
            </>
          ) : (
            <Button type="button" className="ml-auto" onClick={handleDone}>
              Done
            </Button>
          )
        }
      >
        <div className="space-y-4">
          {/* Add-people picker — managers / Team Leaders / the owner, and only
              internal members (the workspace candidate roster can't be fetched
              for an external). Everyone else just views the list below. */}
          {canAddMembers ? (
          <div>
            <label
              htmlFor="space-share-input"
              className="block w-fit mb-1.5 text-sm font-semibold text-[color:var(--color-ink-strong)]"
            >
              Add people from your workspace
            </label>
            <div className="flex items-stretch gap-2">
            <div
              className="flex-1 min-w-0 flex flex-wrap items-center gap-1.5 px-2 py-1.5 rounded-md border bg-[color:var(--color-card)] border-[color:var(--color-rule)] focus-within:border-[color:var(--color-accent)]"
              onClick={() => inputRef.current?.focus()}
            >
              {selected.map((m) => (
                <span
                  key={m.userId}
                  className="inline-flex items-center gap-1.5 pl-1 pr-1 py-0.5 rounded-full bg-[color:var(--color-canvas)] border border-[color:var(--color-rule)] text-sm text-[color:var(--color-ink-strong)]"
                >
                  <span
                    className="h-5 w-5 rounded-full overflow-hidden flex items-center justify-center text-[10px] font-semibold text-white shrink-0"
                    style={{
                      backgroundColor: m.avatarUrl
                        ? 'transparent'
                        : avatarColor(m.fullName),
                    }}
                    aria-hidden="true"
                  >
                    {m.avatarUrl ? (
                      <img
                        src={m.avatarUrl}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      initials(m.fullName)
                    )}
                  </span>
                  {m.fullName}
                  <button
                    type="button"
                    onClick={() => removeChip(m.userId)}
                    aria-label={`Remove ${m.fullName}`}
                    className="p-0.5 rounded-full text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
              <input
                ref={inputRef}
                id="space-share-input"
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (
                    e.key === 'Backspace' &&
                    query === '' &&
                    selected.length > 0
                  ) {
                    removeChip(selected[selected.length - 1].userId)
                  }
                }}
                placeholder={
                  selected.length === 0 ? 'Search by Name or Email ID' : ''
                }
                className="flex-1 min-w-[8rem] outline-none bg-transparent text-sm text-[color:var(--color-ink)] placeholder:text-[color:var(--color-ink-subtle)] py-1"
              />
            </div>

            {/* Access-level picker — sets workspace_members.accessLevel
             *  for each invitee. Only relevant while inviting AND only
             *  available to actors who can actually apply it (workspace
             *  admin or Company Admin). For everyone else the dropdown
             *  is disabled with a hint, so the UI matches what the
             *  backend will permit. */}
            {inviting ? (
              <div className="relative shrink-0">
                <button
                  ref={accessTriggerRef}
                  type="button"
                  aria-haspopup="listbox"
                  aria-expanded={accessMenuOpen}
                  onClick={() => setAccessMenuOpen((v) => !v)}
                  className="h-full inline-flex items-center gap-2 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-sm text-[color:var(--color-ink)] pl-3 pr-2 cursor-pointer hover:bg-[color:var(--color-card-hover)]"
                >
                  <span className="font-medium">
                    {ACCESS_OPTIONS.find((o) => o.value === accessLevel)?.label ?? 'Edit'}
                  </span>
                  <ChevronDown
                    size={14}
                    className={
                      'text-[color:var(--color-ink-muted)] transition-transform ' +
                      (accessMenuOpen ? 'rotate-180' : '')
                    }
                  />
                </button>
                {accessMenuOpen && accessMenuCoords
                  ? createPortal(
                      <div
                        ref={accessMenuRef}
                        role="listbox"
                        aria-label="Access level"
                        // Portaled to <body> with `position: fixed` so
                        // the menu escapes the Modal's scroll-clipped
                        // content area. Without this the dropdown was
                        // visually cut off whenever it extended past
                        // the modal's bottom edge (the bug the user
                        // reported: only "Full edit" + a sliver of
                        // the next row showed). z-index sits above
                        // the Modal's own overlay (Modal uses ~z-50).
                        style={{
                          position: 'fixed',
                          top: accessMenuCoords.top,
                          left: accessMenuCoords.left,
                          width: 288,
                          zIndex: 100,
                        }}
                        className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-2xl overflow-hidden"
                      >
                        {ACCESS_OPTIONS.map((opt) => {
                          const sel = opt.value === accessLevel
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              role="option"
                              aria-selected={sel}
                              onClick={() => {
                                setAccessLevel(opt.value)
                                setAccessMenuOpen(false)
                              }}
                              className={
                                'w-full text-left px-4 py-3 cursor-pointer hover:bg-[color:var(--color-card-hover)] ' +
                                (sel ? 'bg-[color:var(--color-card-hover)]' : '')
                              }
                            >
                              <div className="flex items-start gap-2">
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-semibold text-[color:var(--color-ink-strong)]">
                                    {opt.label}
                                  </p>
                                  <p className="mt-0.5 text-sm text-[color:var(--color-ink-muted)]">
                                    {opt.description}
                                  </p>
                                </div>
                                {sel ? (
                                  <Check
                                    size={14}
                                    className="mt-0.5 text-[color:var(--color-accent-dim)] shrink-0"
                                  />
                                ) : null}
                              </div>
                            </button>
                          )
                        })}
                      </div>,
                      document.body,
                    )
                  : null}
              </div>
            ) : null}
            </div>
            <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
              {spaceQuery.data?.isPrivate
                ? 'Workspace members and your external collaborators can be added here. To add someone who isn’t in the workspace yet, use Invite Members first — people from another workspace or company can’t be added.'
                : 'Only members of this workspace can be added here. To add someone who isn’t in the workspace yet, use Invite Members first — people from another workspace or company can’t be added.'}
            </p>

            {/* Candidate list with checkboxes + "Select all" — tick as many
                people as you like, then Send invites them all at once. The
                search box above filters this list; Select all applies to
                whatever is currently visible. */}
            {candidates.length > 0 ? (
              <div className="mt-2 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] max-h-56 overflow-y-auto">
                <label className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer border-b border-[color:var(--color-rule)] hover:bg-[color:var(--color-card-hover)]">
                  <input
                    ref={(el) => {
                      if (el)
                        el.indeterminate =
                          !allCandidatesSelected && someCandidatesSelected
                    }}
                    type="checkbox"
                    checked={allCandidatesSelected}
                    onChange={toggleSelectAllCandidates}
                    className="shrink-0 accent-[color:var(--color-accent)]"
                  />
                  <span className="font-medium text-[color:var(--color-ink)]">
                    Select all
                  </span>
                  {selected.length > 0 ? (
                    <span className="ml-auto text-xs text-[color:var(--color-ink-muted)]">
                      {selected.length} selected
                    </span>
                  ) : null}
                </label>
                {candidates.map((u) => {
                  const checked = selectedIds.has(u.userId)
                  return (
                    <label
                      key={u.userId}
                      className="w-full flex items-center gap-3 px-3 py-2 text-left cursor-pointer hover:bg-[color:var(--color-card-hover)] border-b border-[color:var(--color-rule)] last:border-b-0"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleCandidate(u)}
                        className="shrink-0 accent-[color:var(--color-accent)]"
                      />
                      <span
                        className="h-8 w-8 rounded-full overflow-hidden flex items-center justify-center text-sm font-semibold text-white shrink-0"
                        style={{
                          backgroundColor: u.avatarUrl
                            ? 'transparent'
                            : avatarColor(u.fullName),
                        }}
                        aria-hidden="true"
                      >
                        {u.avatarUrl ? (
                          <img
                            src={u.avatarUrl}
                            alt=""
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          initials(u.fullName)
                        )}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[color:var(--color-ink-strong)] flex items-center gap-2 min-w-0">
                          <span className="truncate">{u.fullName}</span>
                          {/* Same badge the roster below uses, so a collaborator
                              from outside the company is never added unknowingly. */}
                          {u.external ? (
                            <span
                              className="shrink-0 whitespace-nowrap text-xs uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)]"
                              title="External collaborator — adding them here grants access to this Space only"
                            >
                              External
                            </span>
                          ) : null}
                        </p>
                        <p className="text-sm text-[color:var(--color-ink-muted)] truncate">
                          {u.emailId}
                        </p>
                        <MemberTags u={u} />
                      </div>
                    </label>
                  )
                })}
              </div>
            ) : null}

            {candidates.length === 0 && !wsMembersQuery.isLoading ? (
              <p className="mt-2 text-sm text-[color:var(--color-ink-muted)] italic">
                {query.trim().length > 0
                  ? 'No matches in this workspace.'
                  : 'Everyone who can be added is already on this Space.'}
              </p>
            ) : null}
          </div>
          ) : null}

          {/* People with access — only shown when not inviting. */}
          {!inviting ? (
            <div className="pt-2 border-t border-[color:var(--color-rule)]">
              <p className="pt-3 mb-2 text-sm font-semibold text-[color:var(--color-ink-strong)]">
                People with access
              </p>
              {/* Owner + members live in ONE scrollable list so the
                  scrollbar covers everyone (owner included) and every row
                  shares the same trailing access column — the owner's
                  static label lines up with the members' access picker.
                  The owner sits first with an immutable "Owner" badge. */}
              {membersQuery.isLoading ? (
                <p className="text-sm text-[color:var(--color-ink-muted)]">
                  Loading members…
                </p>
              ) : membersQuery.isError ? (
                <p className="text-sm text-[color:var(--color-priority-urgent)]">
                  {extractApiError(membersQuery.error).message}
                </p>
              ) : !owner && sortedMembers.length === 0 ? (
                <p className="text-sm text-[color:var(--color-ink-muted)]">
                  Workspace admins have access by default. Add specific
                  teammates above to share with anyone else.
                </p>
              ) : (
                <ul className="rounded-md border border-[color:var(--color-rule)] divide-y divide-[color:var(--color-rule)] max-h-72 overflow-y-auto">
                  {owner ? (
                    <li className="flex items-center gap-3 px-3 py-2.5 bg-[color:var(--color-canvas)]">
                      <span
                        className="h-8 w-8 rounded-full overflow-hidden flex items-center justify-center text-sm font-semibold text-white shrink-0"
                        style={{
                          backgroundColor: owner.avatarUrl
                            ? 'transparent'
                            : avatarColor(owner.fullName),
                        }}
                      >
                        {owner.avatarUrl ? (
                          <img
                            src={owner.avatarUrl}
                            alt={owner.fullName}
                            className="h-full w-full object-cover"
                            draggable={false}
                          />
                        ) : (
                          initials(owner.fullName)
                        )}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[color:var(--color-ink-strong)] flex items-center gap-2 min-w-0">
                          <span className="truncate">{owner.fullName}</span>
                          <span
                            className="shrink-0 whitespace-nowrap text-xs uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border border-[color:var(--color-accent-border)]"
                            title="Created the Space"
                          >
                            Owner
                          </span>
                        </p>
                        {owner.emailId ? (
                          <p className="text-sm text-[color:var(--color-ink-muted)] truncate">
                            {owner.emailId}
                          </p>
                        ) : null}
                        <MemberTags u={owner} />
                      </div>
                      {/* Static access label — px-2 + a trash-width spacer
                          so it sits in the same column as the members'
                          access picker below. */}
                      <span
                        className="text-sm font-medium text-[color:var(--color-ink-muted)] px-2 shrink-0"
                        title="The Space owner has full control and can't be changed."
                      >
                        Full edit
                      </span>
                      {anyRemovable ? (
                        <span className="w-[30px] shrink-0" aria-hidden="true" />
                      ) : null}
                    </li>
                  ) : null}
                  {sortedMembers.map((m) => (
                    <li
                      key={m.userId}
                      className="flex items-center gap-3 px-3 py-2.5"
                    >
                      <span
                        className="h-8 w-8 rounded-full overflow-hidden flex items-center justify-center text-sm font-semibold text-white shrink-0"
                        style={{
                          backgroundColor: m.avatarUrl
                            ? 'transparent'
                            : avatarColor(m.fullName),
                        }}
                      >
                        {m.avatarUrl ? (
                          <img
                            src={m.avatarUrl}
                            alt={m.fullName}
                            className="h-full w-full object-cover"
                            draggable={false}
                          />
                        ) : (
                          initials(m.fullName)
                        )}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[color:var(--color-ink-strong)] flex items-center gap-2 min-w-0">
                          <span className="truncate">{m.fullName}</span>
                          {m.status === 'pending' ? (
                            <span
                              className="shrink-0 whitespace-nowrap text-xs uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-[color:var(--color-warning-bg)] text-[color:var(--color-warning)] border border-[color:var(--color-warning-border)]"
                              title="Hasn't accepted the invitation yet"
                            >
                              Pending
                            </span>
                          ) : null}
                          {m.isExternal ? (
                            <span
                              className="shrink-0 whitespace-nowrap text-xs uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)]"
                              title={
                                m.grantSource === 'department'
                                  ? 'External collaborator — reaches this Space through a department grant'
                                  : 'External collaborator — granted access to this Space'
                              }
                            >
                              External
                            </span>
                          ) : null}
                        </p>
                        <p className="text-sm text-[color:var(--color-ink-muted)] truncate">
                          {m.emailId}
                        </p>
                        <MemberTags u={wsMetaByUserId.get(m.userId) ?? m} />
                      </div>
                      {/* Managers / Team Leaders / the owner get the same
                          editable picker for INTERNAL members AND external
                          collaborators. An external's pick is persisted server-
                          side as a seat-scoped, per-Space Capability OVERRIDE
                          (space-members.service `setExternalSpaceAccessLevel`),
                          so it changes their level for THIS Space only and never
                          touches the broader grant or other Spaces. Everyone else
                          sees the level as a static label (mirrors the backend
                          gate). */}
                      {canManageAccess ? (
                        <RowAccessPicker
                          currentLevel={
                            accessByUserId.get(m.userId) ?? m.accessLevel ?? 'edit'
                          }
                          disabled={updateAccessMutation.isPending}
                          onChange={(next) =>
                            updateAccessMutation.mutate({
                              userId: m.userId,
                              accessLevel: next,
                            })
                          }
                        />
                      ) : (
                        <span
                          className="text-sm font-medium text-[color:var(--color-ink-muted)] px-2 shrink-0 cursor-default"
                          title="Only a Manager, Team Leader, or the Space owner can change access."
                        >
                          {/* Non-editable cells read as a state, not an action. */}
                          {ACCESS_OPTIONS.find(
                            (o) =>
                              o.value ===
                              (accessByUserId.get(m.userId) ?? m.accessLevel ?? 'edit'),
                          )?.label ?? 'Edit'}{' '}
                          access
                        </span>
                      )}
                      {/* Remove — managers only, now for EVERY member. For an
                          inherited member on a public Space the server records
                          an exclusion (overrides the department gate) so they
                          lose access to this Space without leaving the dept. */}
                      {canManageAccess ? (
                        <button
                          type="button"
                          onClick={() => setRemoveTarget(m)}
                          disabled={removeMutation.isPending}
                          title={
                            m.userId === actor?.id
                              ? 'Leave Space'
                              : 'Remove from Space'
                          }
                          aria-label={
                            m.userId === actor?.id
                              ? 'Leave Space'
                              : `Remove ${m.fullName} from Space`
                          }
                          className="p-1.5 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-priority-urgent)] hover:bg-[color:var(--color-card-hover)] cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          <Trash2 size={14} />
                        </button>
                      ) : anyRemovable ? (
                        <span className="w-[30px] shrink-0" aria-hidden="true" />
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}
        </div>
      </Modal>

      {/* Confirm before removing — drops their task_assignees too. */}
      <ConfirmDialog
        open={Boolean(removeTarget)}
        title={
          removeTarget?.userId === actor?.id
            ? 'Leave This Space?'
            : 'Remove from Space?'
        }
        message={
          removeTarget
            ? removeTarget.userId === actor?.id
              ? `You'll lose access to "${spaceName}", and any task assignments you have inside this Space will be removed. You'll still be a workspace member.`
              : removeTarget.isExternal
                // An external is not a workspace member, so the usual
                // reassurance would be wrong. Their grant may also be shared
                // with the rest of their agency — say plainly that only this
                // person is affected, because the server records a seat-scoped
                // exclusion rather than revoking the shared grant.
                ? `${removeTarget.fullName} will lose access to "${spaceName}", and any task assignments they have inside it will be removed.${
                    removeTarget.sharedGrant
                      ? ' Other collaborators sharing the same grant keep their access.'
                      : ''
                  }${
                    removeTarget.grantSource === 'department'
                      ? ' They reach it through a department grant, which stays in place for everyone else.'
                      : ''
                  }`
                : `${removeTarget.fullName} will lose access to "${spaceName}", and any task assignments they have inside this Space will be removed. They'll still be a workspace member.`
            : ''
        }
        confirmLabel={
          removeTarget?.userId === actor?.id ? 'Leave' : 'Remove'
        }
        variant="danger"
        confirming={removeMutation.isPending}
        onCancel={() => setRemoveTarget(null)}
        onConfirm={() => {
          if (removeTarget) removeMutation.mutate(removeTarget.userId)
        }}
      />
    </>
  )
}

/** Per-row access level picker — compact button next to the trash icon
 *  that opens a popover. The popover is portalled to <body> so it can
 *  escape the People-with-access list's `overflow-y-auto`, which would
 *  otherwise clip it. Position is computed from the trigger's rect.
 *  Closes on outside click / Escape. */
function RowAccessPicker({
  currentLevel,
  disabled,
  onChange,
}) {
  const [open, setOpen] = useState(false)
  const [coords, setCoords] = useState(null)
  const triggerRef = useRef(null)
  const menuRef = useRef(null)

  // Position the popover relative to the trigger, anchored to its right
  // edge. Opens DOWNWARD by default, but FLIPS UP when there isn't enough
  // room below (e.g. a member row near the bottom of the dialog) so the
  // options never spill past the viewport. Either way the menu height is
  // capped to the available space with internal scrolling, so every
  // option stays reachable.
  useLayoutEffect(() => {
    if (!open) return
    function update() {
      const rect = triggerRef.current?.getBoundingClientRect()
      if (!rect) return
      const GAP = 6
      const MARGIN = 8 // keep the menu off the viewport edge
      const spaceBelow = window.innerHeight - rect.bottom - GAP - MARGIN
      const spaceAbove = rect.top - GAP - MARGIN
      // Flip up only when below is cramped AND above has more room.
      const openUp = spaceBelow < 260 && spaceAbove > spaceBelow
      const maxHeight = Math.max(
        160,
        Math.floor(openUp ? spaceAbove : spaceBelow),
      )
      setCoords({
        right: window.innerWidth - rect.right,
        maxHeight,
        ...(openUp
          ? { bottom: window.innerHeight - rect.top + GAP }
          : { top: rect.bottom + GAP }),
      })
    }
    update()
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    function onClick(e) {
      const t = e.target
      if (triggerRef.current?.contains(t)) return
      if (menuRef.current?.contains(t)) return
      setOpen(false)
    }
    function onKey(e) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])
  const label =
    ACCESS_OPTIONS.find((o) => o.value === currentLevel)?.label ?? 'Edit'
  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex shrink-0 items-center gap-1 px-2 py-1 rounded-md text-sm font-medium text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer disabled:opacity-60"
      >
        {label}
        <ChevronDown
          size={12}
          className={
            'text-[color:var(--color-ink-muted)] transition-transform ' +
            (open ? 'rotate-180' : '')
          }
        />
      </button>
      {open && coords
        ? createPortal(
            <div
              ref={menuRef}
              role="listbox"
              aria-label="Access level"
              style={{
                position: 'fixed',
                // Either `top` (opens down) or `bottom` (flipped up) is
                // set by the positioner above — spreading coords applies
                // whichever is present.
                top: coords.top,
                bottom: coords.bottom,
                right: coords.right,
                maxHeight: coords.maxHeight,
                // Inline overflowY beats the class's `overflow-y:hidden`
                // so the list scrolls when capped; overflow-x stays
                // hidden (from the class) to preserve the rounded corners.
                overflowY: 'auto',
                zIndex: 100,
              }}
              className="w-72 rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-2xl overflow-hidden"
            >
              {ACCESS_OPTIONS.map((opt) => {
                const sel = opt.value === currentLevel
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="option"
                    aria-selected={sel}
                    onClick={() => {
                      setOpen(false)
                      if (!sel) onChange(opt.value)
                    }}
                    className={
                      'w-full text-left px-4 py-3 cursor-pointer hover:bg-[color:var(--color-card-hover)] ' +
                      (sel ? 'bg-[color:var(--color-card-hover)]' : '')
                    }
                  >
                    <div className="flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-[color:var(--color-ink-strong)]">
                          {opt.label}
                        </p>
                        <p className="mt-0.5 text-sm text-[color:var(--color-ink-muted)]">
                          {opt.description}
                        </p>
                      </div>
                      {sel ? (
                        <Check
                          size={14}
                          className="mt-0.5 text-[color:var(--color-accent-dim)] shrink-0"
                        />
                      ) : null}
                    </div>
                  </button>
                )
              })}
            </div>,
            document.body,
          )
        : null}
    </>
  )
}
